import { useMemo } from 'react';
import {
  ArrowRight,
  ArrowRightLeft,
  ChevronRight,
  ClipboardList,
  Clock,
  FlaskConical,
  History,
  Lock,
  MapPin,
  Package,
  PackageCheck,
  RotateCcw,
  Scale,
  TriangleAlert,
  Truck,
  Warehouse
} from 'lucide-react';
import { StatCard } from '@/components/ui/stat-card';
import { StatusPill } from '@/components/ui/status-pill';
import { EmptyState } from '@/components/ui/empty-state';
import { Button } from '@/components/ui/button';
import { ageDays, formatDate, formatNumber, formatPercent } from '@/lib/format';
import { cn } from '@/lib/utils';
import {
  CardFoot,
  CardHead,
  CountPill,
  InvCard,
  LinkButton,
  PersonAvatar
} from './InventoryUi';
import ReorderList from './ReorderList';
import ActiveBookings from './ActiveBookings';
import SiteDeliveriesCard from './SiteDeliveriesCard';
import SiteDemandPanel from './SiteDemandPanel';
import {
  DEFAULT_MIN_LEVEL,
  INVENTORY_LOCATIONS,
  MOVEMENT_KIND_TONE,
  availableOf,
  documentFlowPhrase,
  documentLabel,
  formatQty,
  formatUnitTotals,
  groupMovements,
  isValidPartNumber,
  itemDataIssue,
  locationColor,
  minLevelOf,
  onHandOf,
  pcsLabel,
  plural,
  reorderRows,
  reservedOf,
  shortDate,
  stockMovedParts,
  stockStatus,
  sumByUnit,
  toQty
} from './inventoryModel';

const KIND_ICON = {
  GRN: Package,
  transfer: ArrowRightLeft,
  'site-dn': Truck,
  testing: FlaskConical,
  return: RotateCcw,
  adjustment: Scale
};

/** Days without a receipt before the "Last receipt" badge turns amber. */
const STALE_RECEIPT_DAYS = 30;

export default function DashboardTab({
  stock = [],
  movements = [],
  sitesBoq = [],
  projects = [],
  itemDescriptions = {},
  catalogMeta = null,
  reservations = [],
  reservationsLoading = false,
  reservationsError = '',
  onRetryReservations,
  siteDemandOf = () => 0,
  canEditCatalog = true,
  canEdit = true,
  onSetMin,
  onReserve,
  onIssueBooking,
  onReleaseBooking,
  onNavigate,
  onOpenMovement,
  canRequestPurchase = false,
  onShortageSent,
  onOpenDocument,
  onConfirmDn,
  dnReminders = null
}) {
  // Only movements that changed stock count as history (a direct-to-site DN never did).
  const movedParts = useMemo(() => stockMovedParts(movements), [movements]);
  const projectNames = useMemo(() => new Map(projects.map((project) => [project.id, project.name])), [projects]);

  const summary = useMemo(() => {
    const withStatus = stock.map((item) => ({ item, status: stockStatus(item, movedParts) }));
    const low = withStatus
      .filter((entry) => entry.status === 'low')
      .map((entry) => entry.item)
      // Real part numbers first (data slips such as '10"' are listed under Catalog health), then the
      // biggest gap to the item's low threshold.
      .sort((left, right) => (
        Number(Boolean(itemDataIssue(left))) - Number(Boolean(itemDataIssue(right)))
        || availableOf(left) - availableOf(right)
        || String(left.part_number).localeCompare(String(right.part_number))
      ));
    const onHandItems = stock.filter((item) => onHandOf(item) > 0);
    const reservedItems = stock.filter((item) => reservedOf(item) > 0);
    const byLocation = INVENTORY_LOCATIONS.map((location) => ({
      location,
      qty: stock.reduce((sum, item) => sum + Math.max(0, toQty(item.location_quantities?.[location])), 0)
    }));
    stock.forEach((item) => {
      Object.keys(item.location_quantities || {}).forEach((location) => {
        if (!INVENTORY_LOCATIONS.includes(location) && !byLocation.some((entry) => entry.location === location)) {
          byLocation.push({ location, qty: stock.reduce((sum, row) => sum + Math.max(0, toQty(row.location_quantities?.[location])), 0) });
        }
      });
    });
    return {
      low,
      ranOut: withStatus.filter((entry) => entry.status === 'ran_out').length,
      neverStocked: withStatus.filter((entry) => entry.status === 'never_stocked').length,
      dataIssues: stock.filter((item) => itemDataIssue(item)).length,
      minNotSet: stock.filter((item) => minLevelOf(item) === null).length,
      onHandItems,
      onHandTotal: onHandItems.reduce((sum, item) => sum + onHandOf(item), 0),
      reservedItems,
      reservedTotal: reservedItems.reduce((sum, item) => sum + reservedOf(item), 0),
      byLocation
    };
  }, [movedParts, stock]);

  const boq = useMemo(() => {
    const waiting = sitesBoq.filter((row) => toQty(row.total_remaining_qty) > 0);
    const fractions = sitesBoq
      .filter((row) => toQty(row.total_boq_qty) > 0)
      .map((row) => {
        const delivered = toQty(row.total_delivered_qty ?? (toQty(row.total_boq_qty) - toQty(row.total_remaining_qty)));
        return Math.min(1, Math.max(0, delivered / toQty(row.total_boq_qty)));
      });
    const deliveredPct = fractions.length ? (fractions.reduce((sum, value) => sum + value, 0) / fractions.length) * 100 : 0;
    return {
      waiting,
      deliveredPct,
      remaining: sumByUnit(waiting, (row) => row.total_remaining_qty)
    };
  }, [sitesBoq]);

  const reorder = useMemo(() => reorderRows(stock, movedParts, siteDemandOf), [movedParts, siteDemandOf, stock]);
  const documents = useMemo(() => groupMovements(movements), [movements]);
  const recent = documents.slice(0, 5);
  const lastReceipt = documents.find((group) => group.kind === 'GRN');
  const lastReceiptAge = lastReceipt ? ageDays(lastReceipt.date) : null;
  const earliest = documents.length ? documents[documents.length - 1] : null;
  const linesTotal = documents.reduce((sum, group) => sum + group.lines, 0);
  const inTotal = documents.filter((group) => group.direction > 0).reduce((sum, group) => sum + group.qtyTotal, 0);

  const locationsWithStock = summary.byLocation.filter((entry) => entry.qty > 0).sort((left, right) => right.qty - left.qty);
  const emptyLocations = summary.byLocation.filter((entry) => entry.qty <= 0);
  const onHandLocations = locationsWithStock.length;

  const go = (tab, filters = {}) => onNavigate?.(tab, filters);
  const firstLow = summary.low[0];
  // "Below min level" only where a min level is set; otherwise Low means "below the default 5".
  const lowWithoutMin = summary.low.filter((item) => minLevelOf(item) === null).length;
  const noMinRule = lowWithoutMin === summary.low.length;
  const lowRuleLabel = noMinRule ? `Below ${DEFAULT_MIN_LEVEL}` : 'Below min level';
  const lowRuleTitle = noMinRule
    ? `No min level set — Low means fewer than ${DEFAULT_MIN_LEVEL} available`
    : lowWithoutMin
      ? `Below the item's min level; items without one count as low below ${DEFAULT_MIN_LEVEL}`
      : "Below the item's min level";
  // The Catalog's own count, so the number matches the list "Min level not set" opens.
  const catalogNoMin = catalogMeta?.health?.no_min_level;
  const minNotSet = catalogNoMin ?? summary.minNotSet;

  const locationCard = (
    <InvCard data-testid="inventory-stock-by-location">
      <CardHead
        icon={Warehouse}
        title="Stock by location"
        right={<span className="text-[13px] text-ink-3">{pcsLabel(summary.onHandTotal)}</span>}
      />
      <div className="px-5 pb-2 pt-4">
        <div className="flex h-2.5 gap-0.5 overflow-hidden rounded-full bg-[color:var(--line)]" role="img" aria-label={locationsWithStock.map((entry) => `${entry.location} ${formatQty(entry.qty)} pcs`).join(', ') || 'No stock on hand'}>
          {locationsWithStock.map((entry) => (
            <span
              key={entry.location}
              className="block h-full"
              style={{ width: `${(entry.qty / Math.max(1, summary.onHandTotal)) * 100}%`, background: locationColor(entry.location) }}
            />
          ))}
        </div>
        <ul className="mt-2">
          {locationsWithStock.map((entry) => (
            <li key={entry.location}>
              <button
                type="button"
                onClick={() => go('stock', { view: 'has', location: entry.location })}
                className="group flex w-full items-center gap-2.5 border-b border-line-2 py-2.5 text-left text-[13.5px] text-ink hover:text-brand-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: locationColor(entry.location) }} aria-hidden="true" />
                <span className="min-w-0 flex-1 truncate font-medium">{entry.location}</span>
                <b className="tabular-nums">{pcsLabel(entry.qty)}</b>
                <span className="w-10 text-right text-[12px] tabular-nums text-ink-3">
                  {formatPercent((entry.qty / Math.max(1, summary.onHandTotal)) * 100)}
                </span>
                <ChevronRight className="h-3.5 w-3.5 text-ink-4 group-hover:text-ink-3" aria-hidden="true" />
              </button>
            </li>
          ))}
          {emptyLocations.length ? (
            <li className="flex items-start gap-2.5 py-2.5">
              <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-[color:var(--line)]" aria-hidden="true" />
              <div className="min-w-0">
                <p className="text-[13px] text-ink-2">{plural(emptyLocations.length, 'location')} empty</p>
                <p className="mt-0.5 text-[11.5px] text-ink-3">{emptyLocations.map((entry) => entry.location).join(' · ')}</p>
              </div>
            </li>
          ) : null}
        </ul>
      </div>
    </InvCard>
  );

  const healthCard = (
    <InvCard data-testid="inventory-catalog-health">
      <CardHead
        icon={ClipboardList}
        title="Catalog health"
        right={<LinkButton onClick={() => go('catalog')}>Review in Catalog <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" /></LinkButton>}
      />
      <ul>
        <HealthRow
          label={<>Ran out <span className="text-ink-3">(had stock)</span></>}
          count={summary.ranOut}
          tone={summary.ranOut ? 'red' : 'green'}
          onClick={() => go('stock', { view: 'ran_out' })}
          testId="inventory-health-ran-out"
        />
        <HealthRow
          label="Never stocked"
          sub="No movement ever — not an alert"
          count={summary.neverStocked}
          tone="gray"
          onClick={() => go('stock', { view: 'never' })}
          testId="inventory-health-never"
        />
        <HealthRow
          label="No description / part no."
          count={summary.dataIssues}
          tone={summary.dataIssues ? 'amber' : 'green'}
          onClick={() => go('stock', { view: 'all', needsFix: true })}
          testId="inventory-health-data"
        />
        <HealthRow
          label="Min level not set"
          sub={`${catalogNoMin === undefined ? 'Stock lines' : 'Catalog items'} · Low uses ${DEFAULT_MIN_LEVEL} until a min level is set`}
          count={minNotSet}
          tone="gray"
          onClick={() => go('catalog', { health: 'no_min_level' })}
          testId="inventory-health-min"
        />
      </ul>
</InvCard>
  );

  return (
    <div className="space-y-4" data-testid="inventory-dashboard">
      <div className="grid grid-cols-2 gap-3 sm:gap-4 xl:grid-cols-4">
        <StatCard
          label="Low stock"
          icon={TriangleAlert}
          tone="amber"
          value={formatNumber(summary.low.length, { maxDecimals: 0 })}
          unit={summary.low.length === 1 ? 'item' : 'items'}
          onClick={() => go('stock', { view: 'low' })}
          data-testid="inventory-kpi-low"
          meta={summary.low.length ? (
            <span className="flex min-w-0 items-center gap-2">
              {/* Phones: the card is too narrow for the pill, so the part number keeps the room. */}
              <StatusPill tone="amber" label={lowRuleLabel} title={lowRuleTitle} size="sm" className="hidden shrink-0 sm:inline-flex" data-testid="inventory-kpi-low-rule" />
              <span className="sr-only">{lowRuleTitle}.</span>
              <span className="min-w-[3ch] truncate font-mono text-[11.5px] text-ink-2" title={firstLow.part_number}>{firstLow.part_number}</span>
              {summary.low.length > 1 ? <span className="shrink-0 text-[11.5px] text-ink-3">+{summary.low.length - 1}</span> : null}
            </span>
          ) : 'Nothing is below its min level'}
        />
        <StatCard
          label="Sites waiting"
          icon={MapPin}
          tone="violet"
          value={formatNumber(boq.waiting.length, { maxDecimals: 0 })}
          unit="BOQ lines"
          onClick={() => go('sites-boq', { status: 'all' })}
          data-testid="inventory-kpi-sites"
          progress={boq.deliveredPct}
          progressTone="green"
          progressLabel="BOQ delivered"
          meta={`${formatPercent(boq.deliveredPct)} delivered · ${formatUnitTotals(boq.remaining)} left`}
        />
        <StatCard
          label="Active bookings"
          icon={Lock}
          tone="blue"
          value={formatNumber(summary.reservedItems.length, { maxDecimals: 0 })}
          unit={summary.reservedItems.length ? (summary.reservedItems.length === 1 ? 'item' : 'items') : undefined}
          onClick={() => go('stock', { view: 'reserved' })}
          data-testid="inventory-kpi-bookings"
          meta={summary.reservedItems.length
            ? `${pcsLabel(summary.reservedTotal)} reserved for projects`
            : 'Nothing reserved for projects'}
        />
        <StatCard
          label="On hand"
          icon={PackageCheck}
          tone="green"
          value={formatQty(summary.onHandTotal)}
          unit="pcs"
          onClick={() => go('stock', { view: 'has' })}
          data-testid="inventory-kpi-onhand"
          meta={summary.onHandItems.length
            ? `${plural(summary.onHandItems.length, 'SKU')}${onHandLocations === 1 ? ` · all in ${locationsWithStock[0].location}` : onHandLocations > 1 ? ` · across ${onHandLocations} locations` : ''}`
            : 'No stock on hand'}
        />
      </div>

      <div className="grid items-start gap-4 lg:grid-cols-[minmax(0,1fr)_360px]">
        <ReorderList
          rows={reorder}
          minNotSet={minNotSet}
          canEdit={canEditCatalog}
          canReserve={canEdit}
          onSetMin={onSetMin}
          onReserve={(item) => onReserve?.(item)}
          onOpenStock={() => go('stock', { view: 'low' })}
          onOpenMinLevels={() => go('catalog', { health: 'no_min_level' })}
        />
        <div className="flex min-w-0 flex-col gap-4">
          {locationCard}
          {healthCard}
        </div>
      </div>

      {/* Recent movements | Active bookings: one row of two cards of equal height (the approved layout). */}
      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_360px]">
        <InvCard className="flex flex-col" data-testid="inventory-recent-movements">
          <CardHead
            icon={History}
            title="Recent movements"
            right={lastReceipt ? (
              <StatusPill
                tone={lastReceiptAge !== null && lastReceiptAge > STALE_RECEIPT_DAYS ? 'amber' : 'green'}
                icon={Clock}
                label={`Last receipt ${lastReceiptAge === 0 ? 'today' : `${plural(lastReceiptAge ?? 0, 'day')} ago`}`}
                title={`${documentLabel(lastReceipt)} · ${formatDate(lastReceipt.date)}`}
              />
            ) : (
              <StatusPill tone="gray" label="No receipts recorded yet" />
            )}
          />
          {recent.length ? (
            <ol className="flex-1 px-5 py-2">
              {recent.map((group, index) => {
                const Icon = KIND_ICON[group.kind] || Package;
                // Placeholder part numbers ('-', '10"') are data slips, not parts: never listed as if they were.
                const parts = [...new Set(group.movements.map((movement) => movement.part_number).filter((partNumber) => isValidPartNumber(partNumber)))];
                const withoutPart = group.movements.filter((movement) => !isValidPartNumber(movement.part_number)).length;
                const firstLine = group.movements[0] || {};
                const projectName = projectNames.get(group.projectId);
                return (
                  <li key={group.key} className="relative grid grid-cols-[28px_minmax(0,1fr)] gap-x-3 py-2.5 sm:grid-cols-[28px_minmax(0,1fr)_auto]">
                    {index < recent.length - 1 ? (
                      <span className="absolute bottom-[-6px] left-[13px] top-[38px] w-0.5 rounded bg-line-2" aria-hidden="true" />
                    ) : null}
                    <span className={cn('ui-icon-tile h-7 w-7 rounded-lg', `tone-${MOVEMENT_KIND_TONE[group.kind] || 'gray'}`)} aria-hidden="true">
                      <Icon className="h-3.5 w-3.5" />
                    </span>
                    <div className="min-w-0">
                      <p className="truncate text-[13.5px] text-ink">
                        <b className="font-bold">{documentLabel(group)}</b>
                        <span className="text-ink-2"> · {pcsLabel(group.qtyTotal)} {documentFlowPhrase(group, projectName)}</span>
                      </p>
                      <p className="mt-0.5 truncate text-[12px] text-ink-3">
                        {group.lines === 1 && itemDataIssue({ ...firstLine, description: firstLine.description || itemDescriptions[firstLine.part_number] }) && itemDataIssue({ ...firstLine, description: 'x' }) ? (
                          <span className="inline-flex items-center gap-1 text-status-amber-ink">
                            <span className="font-mono text-[11.5px] text-ink-2">{firstLine.part_number || '—'}</span>
                            <TriangleAlert className="h-3 w-3" aria-hidden="true" />
                            {itemDataIssue({ ...firstLine, description: firstLine.description || itemDescriptions[firstLine.part_number] })}
                          </span>
                        ) : group.lines === 1 ? (
                          <>
                            <span className="font-mono text-[11.5px] text-ink-2">{firstLine.part_number}</span>
                            {(firstLine.description || itemDescriptions[firstLine.part_number]) ? ` ${firstLine.description || itemDescriptions[firstLine.part_number]}` : ''}
                          </>
                        ) : (
                          <>
                            {!parts.length ? null : !withoutPart && parts.length <= 3 && parts.length === group.lines ? (
                              <span className="font-mono text-[11.5px] text-ink-2">{parts.join(', ')}</span>
                            ) : parts.length > 3 ? (
                              plural(group.lines, 'line')
                            ) : (
                              <>
                                <span className="font-mono text-[11.5px] text-ink-2">{parts[0]}</span>
                                {group.lines > 1 ? ` + ${plural(group.lines - 1, 'more line')}` : ''}
                              </>
                            )}
                            {withoutPart ? (
                              <span className="inline-flex items-center gap-1 text-status-amber-ink" data-testid={`recent-no-part-${group.key}`}>
                                {parts.length ? <span className="text-ink-3" aria-hidden="true">&nbsp;·&nbsp;</span> : null}
                                <TriangleAlert className="h-3 w-3" aria-hidden="true" />
                                {withoutPart === 1 ? '1 line without part no.' : `${withoutPart} lines without part no.`}
                              </span>
                            ) : null}
                          </>
                        )}
                      </p>
                    </div>
                    <div className="col-start-2 mt-1 flex items-center gap-2 self-start text-[12px] text-ink-3 sm:col-start-3 sm:row-start-1 sm:mt-0 sm:pt-0.5">
                      {group.user ? <PersonAvatar name={group.user} size={20} /> : null}
                      <span className="whitespace-nowrap">{group.user || '—'} · {shortDate(group.date)}</span>
                    </div>
                  </li>
                );
              })}
            </ol>
          ) : (
            <EmptyState
              icon={History}
              title="No stock movements yet"
              text="Receipts, transfers and site deliveries appear here as soon as they are recorded."
              action={canEdit ? <Button size="sm" variant="outline" onClick={() => onOpenMovement?.('GRN')}><Package aria-hidden="true" /> Record a GRN</Button> : null}
            />
          )}
          {documents.length ? (
            <CardFoot className="mt-auto justify-between">
              <span>
                {plural(documents.length, 'document')} · {plural(linesTotal, 'line')} · {pcsLabel(inTotal)} in
                {earliest ? ` since ${formatDate(earliest.date, { year: 'auto' }).replace(/^\d+\s/, '')}` : ''}
              </span>
              <LinkButton onClick={() => go('movements')}>All movements <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" /></LinkButton>
            </CardFoot>
          ) : null}
        </InvCard>

        <ActiveBookings
          className="h-full"
          reservations={reservations}
          loading={reservationsLoading}
          error={reservationsError}
          onRetry={onRetryReservations}
          canEdit={canEdit}
          onReserve={() => onReserve?.()}
          onIssue={onIssueBooking}
          onRelease={onReleaseBooking}
          onShowAll={() => go('stock', { view: 'reserved' })}
        />
      </div>

      {/* Site demand | Site deliveries — both about the sites; stacked below xl. */}
      <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_360px]">
        <SiteDemandPanel
          className="h-full"
          sitesBoq={sitesBoq}
          stock={stock}
          projects={projects}
          canRequestPurchase={canRequestPurchase}
          onOpenProject={(projectId) => go('sites-boq', { status: 'all', project: projectId })}
          onOpenSitesBoq={() => go('sites-boq', { status: 'all' })}
          onSent={onShortageSent}
        />
        {/* "Record a Site DN" starts from the store that holds the most stock (not a fixed default store). */}
        <SiteDeliveriesCard
          className="h-full"
          movements={movements}
          projects={projects}
          canEdit={canEdit}
          reminders={dnReminders}
          onOpenDocument={(key) => onOpenDocument?.(key)}
          onConfirm={(group) => onConfirmDn?.(group)}
          onRecordSiteDn={() => onOpenMovement?.('issue', [], { sourceLocation: locationsWithStock[0]?.location })}
          onShowAll={() => go('movements', { type: 'site-dn' })}
        />
      </div>
    </div>
  );
}

function HealthRow({ label, sub, count, tone, onClick, testId }) {
  return (
    <li className="border-b border-line-2 last:border-b-0">
      <button
        type="button"
        onClick={onClick}
        data-testid={testId}
        className="flex w-full items-center gap-3 px-5 py-3 text-left hover:bg-surface-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
      >
        <span className="min-w-0 flex-1">
          <span className="block text-[13.5px] text-ink">{label}</span>
          {sub ? <span className="mt-0.5 block text-[11.5px] text-ink-3">{sub}</span> : null}
        </span>
        <CountPill tone={tone}>{formatNumber(count, { maxDecimals: 0 })}</CountPill>
      </button>
    </li>
  );
}
